import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { setFiltersVisibility } from '../../../../../common/actions';
import FilterModal from '../../../../../common/components/FilterModal';
import { SearchableSelect, Dropdown } from '../../../../../common/ui';
import Axios from '../../../../../common/util/Axios';
import styles from './styles.module.css';
import { ORDER_TYPE_LABELS } from '../../utils/orderTypeLabels';
import { formatBusinessVerticalLabel } from '../../utils/businessVerticalLabels';

export const ORDER_TYPE_OPTIONS = Object.entries(ORDER_TYPE_LABELS).map(
  ([value, label]) => ({ value, label })
);

const BillHistoryFilterComp = ({
  common: { isFiltersOpen },
  setFiltersVisibility,
  filters,
  setFilters,
  branchId,
  companyId,
  fromDate,
  toDate,
  isRestaurantCompany = false,
  periodType = '',
  startBusinessDayId = '',
  endBusinessDayId = '',
  listReady = true,
}) => {
  const [cashiers, setCashiers] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [businessVerticals, setBusinessVerticals] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedCashier, setSelectedCashier] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedOrderType, setSelectedOrderType] = useState('');
  const [selectedBusinessVertical, setSelectedBusinessVertical] = useState('');

  const cashiersWithLabel = useMemo(
    () =>
      cashiers.map((c) => ({
        ...c,
        displayLabel: c.name
          ? `${c.name}${c.cashierNo ? ` (${c.cashierNo})` : ''}`
          : c.cashierNo || String(c.id),
      })),
    [cashiers]
  );

  const usersWithLabel = useMemo(() => {
    const list = users.map((u) => ({ ...u, displayLabel: u.name || u.id || '' }));
    if (selectedUser && !list.some((u) => String(u.id) === String(selectedUser.id))) {
      return [{ ...selectedUser, displayLabel: selectedUser.name || selectedUser.id || '' }, ...list];
    }
    return list;
  }, [users, selectedUser]);

  const customersWithLabel = useMemo(() => {
    const list = customers.map((c) => ({ ...c, displayLabel: c.name || c.id || '' }));
    if (selectedCustomer && !list.some((c) => String(c.id) === String(selectedCustomer.id))) {
      return [
        { ...selectedCustomer, displayLabel: selectedCustomer.name || selectedCustomer.id || '' },
        ...list,
      ];
    }
    return list;
  }, [customers, selectedCustomer]);

  const businessVerticalOptions = useMemo(() => {
    const opts = businessVerticals.map((vertical) => ({
      value: vertical,
      label: formatBusinessVerticalLabel(vertical),
    }));
    if (
      selectedBusinessVertical &&
      !opts.some((opt) => String(opt.value) === String(selectedBusinessVertical))
    ) {
      return [
        {
          value: selectedBusinessVertical,
          label: formatBusinessVerticalLabel(selectedBusinessVertical),
        },
        ...opts,
      ];
    }
    return opts;
  }, [businessVerticals, selectedBusinessVertical]);

  useEffect(() => {
    if (!isFiltersOpen || !branchId) return;
    const loadCashiers = async () => {
      try {
        const res = await Axios.get('/api/dashboard/cashiers/find-all-by', {
          params: { branchId },
        });
        setCashiers((res.data && res.data.data) || []);
      } catch (err) {
        console.error('Failed to load cashiers', err);
      }
    };
    loadCashiers();
  }, [isFiltersOpen, branchId]);

  useEffect(() => {
    if (!isFiltersOpen || !companyId || !listReady) {
      return;
    }

    const loadBusinessVerticals = async () => {
      try {
        const params = {
          companyId,
          branchId: branchId || '',
          start_date: fromDate || '',
          end_date: toDate || '',
        };
        if (periodType === 'businessDay') {
          params.periodType = 'businessDay';
          params.startBusinessDayId = startBusinessDayId;
          params.endBusinessDayId = endBusinessDayId;
        }
        const res = await Axios.get('/api/dashboard/bill-history/get-business-verticals', {
          params,
        });
        const data = (res.data && res.data.data) || [];
        setBusinessVerticals(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load business verticals', err);
        setBusinessVerticals([]);
      }
    };

    loadBusinessVerticals();
  }, [
    isFiltersOpen,
    companyId,
    branchId,
    fromDate,
    toDate,
    periodType,
    startBusinessDayId,
    endBusinessDayId,
    listReady,
  ]);

  const searchUsers = useCallback(
    async (keyword) => {
      if (!companyId) return;
      setUsersLoading(true);
      try {
        const params = { companyId, limit: 20, page: 1 };
        if (keyword && keyword.trim()) {
          params.name = { like: `%${keyword.trim()}%` };
        }
        const res = await Axios.get('/api/dashboard/users/find-all', { params });
        setUsers((res.data && res.data.data) || []);
      } catch (err) {
        console.error('Failed to search users', err);
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    },
    [companyId]
  );

  const searchCustomers = useCallback(
    async (keyword) => {
      if (!companyId) return;
      setCustomersLoading(true);
      try {
        const params = {
          companyId,
          searchKeyword: keyword || '',
          limit: 20,
          page: 1,
        };
        const res = await Axios.get('/api/dashboard/customers/find-customers-with-keyword', {
          params,
        });
        setCustomers((res.data && res.data.data) || []);
      } catch (err) {
        console.error('Failed to search customers', err);
        setCustomers([]);
      } finally {
        setCustomersLoading(false);
      }
    },
    [companyId]
  );

  useEffect(() => {
    if (!isFiltersOpen) return;
    setSelectedCashier(filters.cashier || null);
    setSelectedUser(filters.user || null);
    setSelectedCustomer(filters.customer || null);
    setSelectedOrderType(filters.orderType || '');
    setSelectedBusinessVertical(filters.businessVertical || '');
  }, [
    isFiltersOpen,
    filters.cashier,
    filters.user,
    filters.customer,
    filters.orderType,
    filters.businessVertical,
  ]);

  const handleApply = () => {
    const newFilters = {};
    if (selectedCashier) newFilters.cashier = selectedCashier;
    if (selectedUser) newFilters.user = selectedUser;
    if (selectedCustomer) newFilters.customer = selectedCustomer;
    if (selectedBusinessVertical) newFilters.businessVertical = selectedBusinessVertical;
    if (isRestaurantCompany && selectedOrderType) {
      newFilters.orderType = selectedOrderType;
    }
    setFilters(newFilters);
    setFiltersVisibility(false);
  };

  return (
    <FilterModal title="Filter by" onApply={handleApply}>
      <div className={styles.filterOptionName}>Cashier</div>
      <SearchableSelect
        options={cashiersWithLabel}
        getOptionLabel={(o) => o.displayLabel}
        getOptionValue={(o) => o.id}
        value={selectedCashier?.id}
        onChange={(id) => {
          if (!id) setSelectedCashier(null);
        }}
        onSelect={(opt) => setSelectedCashier(opt)}
        placeholder="Select cashier"
      />
      <div className={styles.spacer} />
      <div className={styles.filterOptionName}>User</div>
      <SearchableSelect
        async
        options={usersWithLabel}
        getOptionLabel={(o) => o.displayLabel}
        getOptionValue={(o) => o.id}
        value={selectedUser?.id}
        onChange={(id) => {
          if (!id) setSelectedUser(null);
        }}
        onSelect={(opt) => setSelectedUser(opt)}
        onSearch={searchUsers}
        isLoading={usersLoading}
        placeholder="Search by name"
        minSearchLength={0}
        searchDebounceMs={400}
      />
      <div className={styles.spacer} />
      <div className={styles.filterOptionName}>Customer</div>
      <SearchableSelect
        async
        options={customersWithLabel}
        getOptionLabel={(o) => o.displayLabel}
        getOptionValue={(o) => o.id}
        value={selectedCustomer?.id}
        onChange={(id) => {
          if (!id) setSelectedCustomer(null);
        }}
        onSelect={(opt) => setSelectedCustomer(opt)}
        onSearch={searchCustomers}
        isLoading={customersLoading}
        placeholder="Search by name"
        minSearchLength={0}
        searchDebounceMs={400}
      />
      <div className={styles.spacer} />
      <div className={styles.filterOptionName}>Business Vertical</div>
      <Dropdown
        options={businessVerticalOptions}
        value={selectedBusinessVertical}
        onChange={(val) => setSelectedBusinessVertical(val || '')}
        placeholder="Select business vertical"
      />
      {isRestaurantCompany && (
        <>
          <div className={styles.spacer} />
          <div className={styles.filterOptionName}>Order Type</div>
          <Dropdown
            options={ORDER_TYPE_OPTIONS}
            value={selectedOrderType}
            onChange={(val) => setSelectedOrderType(val || '')}
            placeholder="Select order type"
          />
        </>
      )}
    </FilterModal>
  );
};

BillHistoryFilterComp.propTypes = {
  common: PropTypes.shape({ isFiltersOpen: PropTypes.bool }).isRequired,
  setFiltersVisibility: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  isRestaurantCompany: PropTypes.bool,
  periodType: PropTypes.string,
  startBusinessDayId: PropTypes.string,
  endBusinessDayId: PropTypes.string,
  listReady: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  common: state.commonState,
  branchId: state.authState.branchId,
  companyId: state.authState.companyId,
});

export default connect(mapStateToProps, {
  setFiltersVisibility,
})(BillHistoryFilterComp);
